import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@tabita/i18n';
import type { AccountAmountResponse } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { financeApi } from '@/services/finance.api';
import { ApiError } from '@/services/api-client';
import { Alert, EmptyState, Field, Spinner } from '@/components/ui';

function today(): string {
  return new Date(Date.now()).toISOString().slice(0, 10);
}

function firstOfMonth(): string {
  const now = new Date(Date.now());
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

function AmountTable({
  rows,
  total,
  totalLabel,
  emptyLabel,
  locale,
}: {
  readonly rows: readonly AccountAmountResponse[];
  readonly total: number;
  readonly totalLabel: string;
  readonly emptyLabel: string;
  readonly locale: string;
}) {
  if (rows.length === 0) return <EmptyState title={emptyLabel} />;

  return (
    <div className="table-scroll">
      <table className="table">
        <tbody>
          {rows.map((row) => (
            <tr key={row.accountId}>
              <td>
                {row.name} <span className="cell-mono">{row.code}</span>
              </td>
              <td dir="ltr">{formatMoney(row.amount, 'USD', locale)}</td>
            </tr>
          ))}
          <tr>
            <th scope="row">{totalLabel}</th>
            <td dir="ltr">
              <strong>{formatMoney(total, 'USD', locale)}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/**
 * Read-only reports over the ledger `chart-of-accounts-page.tsx` and
 * `journal-entries-page.tsx` already build — no new financial data, just a
 * summarized view of what `postJournalEntry` (staff and the DonationPosted
 * consumer alike) already posted.
 */
export function FinancialReportsPage() {
  const { t, locale } = useI18n();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [asOf, setAsOf] = useState(today());

  const incomeStatement = useQuery({
    queryKey: ['finance', 'reports', 'income-statement', from, to],
    queryFn: ({ signal }) => financeApi.getIncomeStatement(from, to, signal),
    enabled: from <= to,
  });

  const fundBalances = useQuery({
    queryKey: ['finance', 'reports', 'fund-balances', from, to],
    queryFn: ({ signal }) => financeApi.getFundBalances(from, to, signal),
    enabled: from <= to,
  });

  const balanceSheet = useQuery({
    queryKey: ['finance', 'reports', 'balance-sheet', asOf],
    queryFn: ({ signal }) => financeApi.getBalanceSheet(asOf, signal),
  });

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">{t('finance.reports.title')}</h1>
      </header>

      <form className="filters">
        <Field
          label={t('finance.reports.from')}
          name="reports-from"
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
        <Field
          label={t('finance.reports.to')}
          name="reports-to"
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
      </form>
      {from > to && <Alert tone="warning">{t('finance.reports.invalidRange')}</Alert>}

      <section className="detail-section">
        <h2 className="detail-section-title">{t('finance.reports.fundBalances')}</h2>
        {fundBalances.isPending && <Spinner label={t('common.states.loading')} />}
        {fundBalances.isError && <Alert tone="danger">{errorMessage(fundBalances.error, t)}</Alert>}
        {fundBalances.data && (
          <AmountTable
            rows={fundBalances.data.funds}
            total={fundBalances.data.total}
            totalLabel={t('finance.reports.total')}
            emptyLabel={t('finance.reports.empty')}
            locale={locale}
          />
        )}
      </section>

      <section className="detail-section">
        <h2 className="detail-section-title">{t('finance.reports.incomeStatement')}</h2>
        {incomeStatement.isPending && <Spinner label={t('common.states.loading')} />}
        {incomeStatement.isError && (
          <Alert tone="danger">{errorMessage(incomeStatement.error, t)}</Alert>
        )}
        {incomeStatement.data && (
          <>
            <h3 className="settings-status-title">{t('finance.reports.revenue')}</h3>
            <AmountTable
              rows={incomeStatement.data.revenue}
              total={incomeStatement.data.totalRevenue}
              totalLabel={t('finance.reports.totalRevenue')}
              emptyLabel={t('finance.reports.empty')}
              locale={locale}
            />
            <h3 className="settings-status-title">{t('finance.reports.expense')}</h3>
            <AmountTable
              rows={incomeStatement.data.expense}
              total={incomeStatement.data.totalExpense}
              totalLabel={t('finance.reports.totalExpense')}
              emptyLabel={t('finance.reports.empty')}
              locale={locale}
            />
            <p className="page-count">
              {t('finance.reports.netIncome')}:{' '}
              <strong dir="ltr">
                {formatMoney(incomeStatement.data.netIncome, 'USD', locale)}
              </strong>
            </p>
          </>
        )}
      </section>

      <section className="detail-section">
        <h2 className="detail-section-title">{t('finance.reports.balanceSheet')}</h2>
        <Field
          label={t('finance.reports.asOf')}
          name="reports-as-of"
          type="date"
          value={asOf}
          onChange={(event) => setAsOf(event.target.value)}
        />
        {balanceSheet.isPending && <Spinner label={t('common.states.loading')} />}
        {balanceSheet.isError && <Alert tone="danger">{errorMessage(balanceSheet.error, t)}</Alert>}
        {balanceSheet.data && (
          <>
            <h3 className="settings-status-title">{t('finance.reports.assets')}</h3>
            <AmountTable
              rows={balanceSheet.data.assets}
              total={balanceSheet.data.totalAssets}
              totalLabel={t('finance.reports.totalAssets')}
              emptyLabel={t('finance.reports.empty')}
              locale={locale}
            />
            <h3 className="settings-status-title">{t('finance.reports.liabilities')}</h3>
            <AmountTable
              rows={balanceSheet.data.liabilities}
              total={balanceSheet.data.totalLiabilities}
              totalLabel={t('finance.reports.totalLiabilities')}
              emptyLabel={t('finance.reports.empty')}
              locale={locale}
            />
            <h3 className="settings-status-title">{t('finance.reports.equity')}</h3>
            <AmountTable
              rows={balanceSheet.data.equity}
              total={balanceSheet.data.totalEquity}
              totalLabel={t('finance.reports.totalEquity')}
              emptyLabel={t('finance.reports.empty')}
              locale={locale}
            />
          </>
        )}
      </section>
    </div>
  );
}
